'use client';

import { format, parseISO } from 'date-fns';
import { tz } from '@date-fns/tz';
import type { ShippingPolicyItem } from '../../model/siteconfig/siteconfig';

/**
 * Which data source backed the SLA block:
 * - `measured`: Tier 1 — `dynamicSla.p20Date` / `p90Date` from recent delivery samples.
 * - `fallback`: Tier 2 — `deliveryDateRange.minDate` / `maxDate` derived from the
 *   static shipping duration. Same UI shape, different source.
 */
export const SHIPPING_SLA_TIER = {
  Measured: 'measured',
  Fallback: 'fallback',
} as const;

export type ShippingSlaTier = (typeof SHIPPING_SLA_TIER)[keyof typeof SHIPPING_SLA_TIER];

export interface FormattedShippingSLA {
  /** Data source used to derive the dates */
  tier: ShippingSlaTier;
  /** Formatted P20 (or fallback min) date, e.g. "Apr 22" */
  earliestDateText: string;
  /** Formatted P90 (or fallback max) date, e.g. "Apr 28" */
  latestDateText: string;
  /** Seasonal notice line (policy.systemMessage) — undefined when not set */
  seasonalNoticeText?: string;
}

const SLA_DATE_FORMAT = 'MMM d';

/**
 * Parses a backend SLA date (`YYYY-MM-DD`) as UTC midnight. Without the
 * explicit `Z` suffix, `parseISO` treats a bare date as local time, which
 * shifts the calendar day when the runtime timezone differs from the
 * display timezone. The SLA value is a calendar date, not a timestamp —
 * we want the literal day back.
 */
const parseSlaDate = (isoDate: string) => parseISO(`${isoDate}T00:00:00Z`);

const formatSlaDate = (isoDate: string) => format(parseSlaDate(isoDate), SLA_DATE_FORMAT, { in: tz('UTC') });

/**
 * Display helper — collapses a date range to a single date when the two
 * formatted strings are identical, otherwise joins with the given separator.
 *
 * Used by the default + summary + mobile variants so the collapse rule is
 * defined once. Pass `'–'` (en dash) for the new measured-tier range and
 * `'-'` (hyphen-minus) for the legacy fallback copy.
 */
export const formatSlaDateRange = (earliest: string, latest: string, separator: string): string =>
  earliest === latest ? earliest : `${earliest} ${separator} ${latest}`;

/**
 * Derives the Dynamic SLA block (SSD-3986 Phase 2) from a `/shippingPolicies`
 * entry. Returns `undefined` when neither a dynamic SLA nor a delivery date
 * range is available — in that case consumers should render nothing.
 *
 * Tier 1 (`dynamicSla.p20Date` + `p90Date` with `p20Date <= p90Date`) → uses
 * measured percentile dates.
 * Tier 2 (everything else, while `deliveryDateRange` is present) → uses
 * `deliveryDateRange.minDate` / `maxDate`. Same return shape, different `tier`.
 */
export const formatShippingSLA = (policy: ShippingPolicyItem | undefined): FormattedShippingSLA | undefined => {
  if (!policy) {
    return undefined;
  }

  const seasonalNoticeText = policy.systemMessage?.trim() ? policy.systemMessage.trim() : undefined;

  const { p20Date, p90Date } = policy.dynamicSla ?? {};

  // ISO YYYY-MM-DD strings are lexicographically sortable, so the inequality
  // doubles as a sanity check that rejects an inverted range.
  if (p20Date && p90Date && p20Date <= p90Date) {
    return {
      tier: SHIPPING_SLA_TIER.Measured,
      earliestDateText: formatSlaDate(p20Date),
      latestDateText: formatSlaDate(p90Date),
      seasonalNoticeText,
    };
  }

  if (policy.deliveryDateRange) {
    return {
      tier: SHIPPING_SLA_TIER.Fallback,
      earliestDateText: formatSlaDate(policy.deliveryDateRange.minDate),
      latestDateText: formatSlaDate(policy.deliveryDateRange.maxDate),
      seasonalNoticeText,
    };
  }

  return undefined;
};
